import { Delete } from "@mui/icons-material";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { captureException } from "@sentry/react";
import { createFileRoute, createLink } from "@tanstack/react-router";

import { PlayerHead } from "#components/player.tsx";
import { UserSearch } from "#components/UserSearch.tsx";
import { useCurrentUser } from "#contexts/CurrentUser/hooks.ts";
import { usePlayerVisits } from "#contexts/PlayerVisits/hooks.ts";
import { useUUIDToUsername } from "#queries/username.ts";

const RouterLinkButton = createLink(Button);

export const Route = createFileRoute("/")({
    // oxlint-disable-next-line eslint/no-use-before-define
    component: RouteComponent,
});

function RouteComponent() {
    const navigate = Route.useNavigate();
    const { currentUser } = useCurrentUser();
    const { favoriteUUIDs, removePlayerVisits } = usePlayerVisits();
    const favorites =
        currentUser !== null
            ? [
                  currentUser,
                  ...favoriteUUIDs.filter((uuid) => uuid !== currentUser),
              ].slice(0, 5)
            : favoriteUUIDs.slice(0, 5);
    const uuidToUsername = useUUIDToUsername(favorites);

    return (
        <Stack
            height="100%"
            width="100%"
            justifyContent="center"
            alignItems="center"
            gap={2}
        >
            <meta
                name="description"
                content="The Prism Overlay for Hypixel Bedwars shows the stats of all players in your game and automatically tracks your progress. View your session stats and compare your stats with other players."
            />
            <link rel="canonical" href="https://prismoverlay.com" />
            <UserSearch
                onSubmit={(uuid) => {
                    void (async () => {
                        try {
                            await navigate({
                                to: "/session/$uuid",
                                params: { uuid },
                                search: {
                                    gamemode: "overall",
                                    stat: "fkdr",
                                    timeIntervalDefinition: { type: "contained" },
                                    variantSelection: "both",
                                    sessionTableMode: "total",
                                    showExtrapolatedSessions: false,
                                },
                            });
                        } catch (error: unknown) {
                            captureException(error, {
                                tags: {
                                    param: "gamemode",
                                },
                                extra: {
                                    message: "Failed to update search params",
                                    gamemode: "overall",
                                },
                            });
                        }
                    })();
                }}
                size="medium"
            />
            {favorites.length > 0 && (
                <Stack direction="row" gap={1}>
                    {favorites.map((uuid) => (
                        <RouterLinkButton
                            sx={{
                                minWidth: 80,
                                textTransform: "none",
                                [`& #delete-favorite-${uuid}`]: {
                                    transition: "opacity 0.2s",
                                    opacity: 0,
                                },
                                [`&:hover #delete-favorite-${uuid}`]: {
                                    transition: "opacity 0.2s",
                                    opacity: 1,
                                },
                            }}
                            key={uuid}
                            to="/session/$uuid"
                            params={{ uuid }}
                            search={{
                                gamemode: "overall",
                                stat: "fkdr",
                                timeIntervalDefinition: { type: "contained" },
                                variantSelection: "both",
                                sessionTableMode: "total",
                                showExtrapolatedSessions: false,
                            }}
                        >
                            <Stack alignItems="center" gap={1}>
                                <PlayerHead
                                    uuid={uuid}
                                    username={uuidToUsername[uuid]}
                                    variant="cube"
                                />
                                <Typography variant="body2">
                                    {uuidToUsername[uuid]}
                                </Typography>
                            </Stack>
                            <IconButton
                                id={`delete-favorite-${uuid}`}
                                sx={{
                                    position: "absolute",
                                    top: -5,
                                    right: -5,
                                    "&:hover": {
                                        transition: "opacity 0.2s",
                                        opacity: "1!important",
                                    },
                                    "&:focus": {
                                        transition: "opacity 0.2s",
                                        opacity: "1!important",
                                    },
                                }}
                                size="small"
                                color="error"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    removePlayerVisits(uuid);
                                }}
                            >
                                <Delete
                                    fontSize="small"
                                    aria-label={`Remove ${uuidToUsername[uuid] ?? "unknown"} from favorites`}
                                />
                            </IconButton>
                        </RouterLinkButton>
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
